<template>
    <FilterDialog
        v-if="variant === 'dropdown'"
        label="Вид игры"
        :summary="summaryText"
        :disabled="disabled"
        @open="onOpen"
        @apply="onApply"
        @cancel="onCancel"
    >
        <CompetitionGroups
            :groups="competitionStageGroups"
            :pending-types="pendingTypes"
            :pending-stages="pendingStages"
            :disabled="disabled"
            :is-group-fully-selected="isCompetitionGroupFullySelected"
            @toggle-type="toggleCompetitionType"
            @toggle-group="toggleCompetitionGroup"
            @toggle-stage="toggleStage"
        />
    </FilterDialog>

    <div
        v-else
        class="competition-stage-select competition-stage-select--inline"
    >
        <div class="competition-stage-select__label">Вид игры</div>
        <CompetitionGroups
            :groups="competitionStageGroups"
            :pending-types="pendingTypes"
            :pending-stages="pendingStages"
            :disabled="disabled"
            :is-group-fully-selected="isCompetitionGroupFullySelected"
            @toggle-type="toggleCompetitionType"
            @toggle-group="toggleCompetitionGroup"
            @toggle-stage="toggleStage"
        />
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import CompetitionGroups from '@/components/filters/CompetitionStageGroups.vue';
import FilterDialog from '@/components/ui/forms/FilterDialog.vue';
import {
    getCompetitionStageGroups,
    getStagesForCompetitionTypes,
    parseStageFilterKey,
} from '@/utils/skillQualityConfig';

const props = defineProps({
    competitionTypes: { type: Array, default: () => [] },
    stages: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    variant: {
        type: String,
        default: 'dropdown',
        validator: (value) => ['dropdown', 'inline'].includes(value),
    },
});

const emit = defineEmits(['change']);

const competitionStageGroups = getCompetitionStageGroups();
const pendingTypes = ref([]);
const pendingStages = ref([]);
const isOpen = ref(false);

const isInline = computed(() => props.variant === 'inline');

const allStageValues = computed(() =>
    competitionStageGroups.flatMap((group) => group.stages.map((stage) => stage.value))
);

const allTypeValues = computed(() => competitionStageGroups.map((group) => group.value));

const summaryText = computed(() => {
    const types = isOpen.value ? pendingTypes.value : props.competitionTypes;
    const selectedStages = isOpen.value ? pendingStages.value : props.stages;

    if (!types.length && !selectedStages.length) {
        return 'Нет турниров';
    }

    const isAllTypes = allTypeValues.value.every((type) => types.includes(type));
    const isAllStages =
        allStageValues.value.length === 0 ||
        allStageValues.value.every((stage) => selectedStages.includes(stage));

    if (isAllTypes && isAllStages) {
        return 'Все турниры';
    }

    if (types.length === 1 && !selectedStages.length) {
        const group = competitionStageGroups.find((item) => item.value === types[0]);
        return group?.label || '1 турнир';
    }

    if (selectedStages.length === 1 && types.length <= 1) {
        const stage = competitionStageGroups
            .flatMap((group) => group.stages)
            .find((item) => item.value === selectedStages[0]);
        return stage?.label || '1 этап';
    }

    const selectedCount =
        types.filter((type) => {
            const group = competitionStageGroups.find((item) => item.value === type);
            return group && !group.stages.length;
        }).length + selectedStages.length;

    return `${selectedCount} выбрано`;
});

function arraysEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((item) => b.includes(item));
}

function emitChange(nextTypes, nextStages) {
    emit('change', {
        competitionTypes: [...nextTypes],
        stages: [...nextStages],
    });
}

function commitPendingIfDirty() {
    if (
        arraysEqual(pendingTypes.value, props.competitionTypes)
        && arraysEqual(pendingStages.value, props.stages)
    ) {
        return;
    }

    emitChange(pendingTypes.value, pendingStages.value);
}

function syncFromProps() {
    pendingTypes.value = [...props.competitionTypes];
    pendingStages.value = [...props.stages];
}

watch(
    () => [props.competitionTypes, props.stages],
    () => {
        if (isInline.value || !isOpen.value) {
            syncFromProps();
        }
    },
    { deep: true, immediate: true }
);

function onOpen() {
    syncFromProps();
    isOpen.value = true;
}

function onApply() {
    isOpen.value = false;
    commitPendingIfDirty();
}

function onCancel() {
    isOpen.value = false;
    syncFromProps();
}

function competitionGroupStageValues(group) {
    return group.stages.map((stage) => stage.value);
}

function isCompetitionGroupFullySelected(group) {
    const stageValues = competitionGroupStageValues(group);
    const typeSelected = pendingTypes.value.includes(group.value);

    if (!typeSelected) {
        return false;
    }

    if (!stageValues.length) {
        return true;
    }

    return stageValues.every((stage) => pendingStages.value.includes(stage));
}

function toggleCompetitionType(type) {
    let nextTypes = [...pendingTypes.value];
    let nextStages = [...pendingStages.value];
    const index = nextTypes.indexOf(type);

    if (index >= 0) {
        nextTypes = nextTypes.filter((item) => item !== type);
        const available = getStagesForCompetitionTypes([type]).map((stage) => stage.value);
        nextStages = nextStages.filter((stage) => !available.includes(stage));
    } else {
        nextTypes = [...nextTypes, type];
        const stageValues = getStagesForCompetitionTypes([type]).map((stage) => stage.value);
        const without = nextStages.filter((stage) => !stageValues.includes(stage));
        nextStages = [...without, ...stageValues];
    }

    pendingTypes.value = nextTypes;
    pendingStages.value = nextStages;

    if (isInline.value) {
        emitChange(nextTypes, nextStages);
    }
}

function toggleCompetitionGroup(group) {
    const stageValues = competitionGroupStageValues(group);
    let nextTypes = [...pendingTypes.value];
    let nextStages = [...pendingStages.value];

    if (isCompetitionGroupFullySelected(group)) {
        nextTypes = nextTypes.filter((type) => type !== group.value);
        nextStages = nextStages.filter((stage) => !stageValues.includes(stage));
    } else {
        if (!nextTypes.includes(group.value)) {
            nextTypes = [...nextTypes, group.value];
        }
        const stagesWithoutGroup = nextStages.filter((stage) => !stageValues.includes(stage));
        nextStages = [...stagesWithoutGroup, ...stageValues];
    }

    pendingTypes.value = nextTypes;
    pendingStages.value = nextStages;

    if (isInline.value) {
        emitChange(nextTypes, nextStages);
    }
}

function toggleStage(stageValue) {
    let nextTypes = [...pendingTypes.value];
    let nextStages = [...pendingStages.value];
    const index = nextStages.indexOf(stageValue);

    if (index >= 0) {
        nextStages = nextStages.filter((stage) => stage !== stageValue);
    } else {
        nextStages = [...nextStages, stageValue];
        const { competitionType } = parseStageFilterKey(stageValue);
        if (competitionType && !nextTypes.includes(competitionType)) {
            nextTypes = [...nextTypes, competitionType];
        }
    }

    pendingTypes.value = nextTypes;
    pendingStages.value = nextStages;

    if (isInline.value) {
        emitChange(nextTypes, nextStages);
    }
}
</script>
